import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { MiraAvatar } from "@/components/MiraAvatar";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight, MessageSquareText, TrendingDown, Ghost, Clock, ShieldCheck,
  CheckCircle2, Target, Repeat, Calculator, Sparkles,
} from "lucide-react";

const DOJO_IMG =
  "https://images.unsplash.com/photo-1764616211830-993b5e360d82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtYXJ0aWFsJTIwYXJ0cyUyMGRvam8lMjB0cmFpbmluZyUyMGtpZHMlMjBrYXJhdGV8ZW58MHx8fHwxNzg4Mjc2NDU2fDA&ixlib=rb-4.1.0&q=85";

const PROBLEMS = [
  { icon: Ghost, title: "Leads that ghost you", desc: "You pay for the clicks, the form fills come in… and then most of them never reply, never book, never show. The ad money just evaporates." },
  { icon: TrendingDown, title: "One step forward, two back", desc: "You enroll a few new students this month, then quietly lose almost as many. Your mat count — and your revenue — stays stuck in the same place all year." },
  { icon: Clock, title: "Doing it all yourself", desc: "You teach every class, chase every lead, and handle the follow-up at 9pm. There's no system running quietly in the background keeping you full." },
];

const STEPS = [
  { n: "01", title: "Tell Mira your numbers", desc: "A quick, friendly chat — students, goal, ad spend, leads, how many book, show, and enroll. No spreadsheets. Just answer as you go." },
  { n: "02", title: "She finds the leak", desc: "Mira compares your funnel against what's working for schools like yours and pinpoints the exact stage costing you the most students and money." },
  { n: "03", title: "Get your 12-month plan", desc: "A clear, custom growth plan: what to spend, the numbers to hit at each step, and the fix that gets you to your goal — yours to keep and revisit." },
];

const DELIVERABLES = [
  { icon: Target, title: "Your ad-spend roadmap", desc: "Exactly how much to invest each month to hit your 12-month student goal — worked backwards from the numbers you gave her." },
  { icon: Calculator, title: "Your funnel, stage by stage", desc: "Leads → booked intros → show-ups → paid trials → long-term members, with the target at every step so you know what 'done right' looks like." },
  { icon: Repeat, title: "The self-funding flywheel", desc: "How your trial income can cover your ad spend that same month — so growth pays for itself instead of draining your bank account." },
  { icon: Sparkles, title: "The one lever to fix first", desc: "The single weakest spot in your business right now — named in plain English — and what to do about it before anything else." },
];

const FAQS = [
  { q: "Is this really free?", a: "Yes. The chat with Mira and your full growth plan are 100% free. No credit card, no trial that bills you later. We'd rather earn your trust by handing you something genuinely useful first." },
  { q: "Do I have to get on a sales call?", a: "Nope. It's just a chat. Mira asks a few questions and builds your plan right there. If — and only if — you'd like help implementing it, she'll point you to the right resource. No pressure, no pushy call." },
  { q: "How long does it take?", a: "About 5 minutes. It's a natural back-and-forth, one question at a time — not a giant form. Most owners are surprised how fast (and how eye-opening) it is." },
  { q: "Will this work for my kind of school?", a: "It's built for martial arts schools, gyms, and studios of every style — karate, BJJ, taekwondo, kickboxing, fitness. Mira adapts to how you actually run classes." },
  { q: "What do you do with my numbers?", a: "We use them to build your plan and stay in touch about it — that's it. Your information is never sold. You're always in control." },
];

export default function MarketingLanding() {
  const navigate = useNavigate();
  const go = () => navigate("/chat");

  useEffect(() => { document.title = "Mira — Your Free Studio Growth Plan"; }, []);

  const Cta = ({ label = "Get my free growth plan", testid, className = "" }) => (
    <Button
      data-testid={testid}
      onClick={go}
      className={`group h-auto bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-base px-7 py-4 rounded-xl shadow-[0_0_25px_rgba(16,185,129,0.35)] hover:shadow-[0_0_38px_rgba(16,185,129,0.6)] transition-all hover:-translate-y-0.5 ${className}`}
    >
      {label}
      <ArrowRight className="h-5 w-5 ml-1.5 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
    </Button>
  );

  return (
    <div className="min-h-screen bg-background mm-grid-bg text-slate-100">
      {/* Sticky header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div data-testid="header-logo"><Logo size="md" /></div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-400">
            <a href="#how" className="hover:text-emerald-300 transition-colors">How it works</a>
            <a href="#plan" className="hover:text-emerald-300 transition-colors">What you get</a>
            <a href="#why" className="hover:text-emerald-300 transition-colors">Why free</a>
            <a href="#faq" className="hover:text-emerald-300 transition-colors">FAQ</a>
          </nav>
          <Button data-testid="header-cta-button" onClick={go}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold rounded-xl px-4 sm:px-5 h-10">
            Chat with Mira
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 sm:pt-36 pb-20 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7 space-y-6 mm-fade-in">
            <span className="inline-flex items-center gap-2 text-xs font-mono font-semibold uppercase tracking-[0.2em] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1.5">
              For martial arts, gym &amp; studio owners
            </span>
            <h1 data-testid="hero-headline" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              You're a great instructor.<br className="hidden sm:block" /> That's <span className="text-slate-400">not</span> the problem.<br className="hidden sm:block" />
              Your <span className="text-emerald-400">growth system</span> is.
            </h1>
            <p data-testid="hero-subheadline" className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
              Spend 5 minutes chatting with <span className="text-slate-100 font-semibold">Mira</span>, your growth advisor. Tell her your real numbers and she'll show you exactly where students and money are leaking out — then build you a custom <span className="text-slate-100 font-semibold">12-month growth plan</span>. Free. No call. No catch.
            </p>
            <div className="space-y-3">
              <Cta testid="hero-chat-mira-cta-button" label="Get my free growth plan" className="w-full sm:w-auto" />
              <p className="text-xs sm:text-sm text-slate-500 font-medium flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-400" /> 100% free · No credit card · No sales call · ~5 minutes
              </p>
            </div>
            <div className="pt-2 flex items-center gap-3 text-sm text-slate-400">
              <MessageSquareText className="h-4 w-4 text-emerald-400 shrink-0" />
              From the team behind <span className="text-slate-200 font-semibold">The Follow Up Ninja</span> &amp; Student Magnet+.
            </div>
          </div>

          {/* Mira profile card */}
          <div className="lg:col-span-5 relative mm-fade-in">
            <div className="absolute -inset-4 bg-emerald-500/10 blur-3xl rounded-full" aria-hidden />
            <div className="relative rounded-3xl border border-emerald-500/30 bg-slate-900/70 backdrop-blur-md p-6 shadow-2xl shadow-emerald-950/40">
              <div className="flex items-center gap-4">
                <MiraAvatar className="h-16 w-16" />
                <div>
                  <div className="font-display text-xl font-bold">Mira</div>
                  <div className="text-sm text-slate-400">Senior Growth Advisor</div>
                  <span className="mt-1 inline-flex items-center gap-1.5 text-xs font-mono text-emerald-400">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> Online now
                  </span>
                </div>
              </div>
              <div className="mt-5 space-y-3">
                <div className="bg-slate-950/70 border border-slate-800 rounded-2xl rounded-bl-md px-4 py-3 text-[15px] text-slate-200">
                  Hey! I help school owners fill their classes. Where should I send your free growth plan?
                </div>
                <div className="flex justify-end">
                  <div className="bg-emerald-500 text-slate-950 font-medium rounded-2xl rounded-br-md px-4 py-3 text-[15px]">
                    Let's do it 👊
                  </div>
                </div>
              </div>
              <Button data-testid="hero-card-cta" onClick={go}
                className="mt-5 w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold rounded-xl">
                Start the chat <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={2.5} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem agitation */}
      <section className="py-20 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight text-center max-w-2xl mx-auto">
            Sound painfully familiar?
          </h2>
          <p className="mt-3 text-center text-slate-400 max-w-xl mx-auto">If you've felt any of these, you're not bad at business — you just never had a system built to fix them.</p>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {PROBLEMS.map((p) => (
              <div key={p.title} data-testid="problem-section-card"
                className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 sm:p-7 hover:border-emerald-500/40 transition-all duration-300">
                <div className="h-11 w-11 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mb-4">
                  <p.icon className="h-5 w-5 text-rose-400" />
                </div>
                <h3 className="text-lg font-bold tracking-tight">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chat preview mockup */}
      <section className="py-8 px-4 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <p className="text-center text-xs font-mono font-semibold uppercase tracking-[0.2em] text-emerald-400 mb-3">A 5-minute conversation</p>
          <h2 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight text-center mb-8">What talking to Mira looks like</h2>
          <div data-testid="chat-preview-box" className="rounded-3xl border border-slate-800 bg-slate-950/60 backdrop-blur-xl p-5 sm:p-6 shadow-2xl space-y-4">
            <Bubble who="mira">How many new leads reach out in a typical month, and how many of them actually book an intro?</Bubble>
            <Bubble who="user">About 60 leads. Maybe 15 book, and only half of those show up.</Bubble>
            <Bubble who="mira">There it is. Your ads are working — it's the follow-up between "lead" and "booked" that's leaking. Fix that one step and you'd roughly double your intros without spending a dollar more. Here's the plan…</Bubble>
          </div>
          <div className="text-center mt-8"><Cta testid="preview-cta" label="Ask Mira about your studio" /></div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-20 px-4 sm:px-8 bg-slate-900/40 border-y border-slate-800/80 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight text-center">Three steps to your growth plan</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.n} data-testid={`how-it-works-step-${i + 1}`}
                className="relative bg-slate-900/70 border border-slate-800 rounded-2xl p-7 hover:border-emerald-500/40 transition-all duration-300">
                <div className="font-display text-5xl font-extrabold text-emerald-500/25">{s.n}</div>
                <h3 className="mt-2 text-lg font-bold tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value stack */}
      <section id="plan" className="py-20 px-4 sm:px-8 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-emerald-400 mb-3">Your free plan includes</p>
            <h2 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight">A real roadmap — not a generic PDF</h2>
            <p className="mt-3 text-slate-400">Every plan is built from your numbers and your 12-month goal. Here's what you walk away with:</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {DELIVERABLES.map((d) => (
              <div key={d.title} data-testid="value-stack-item"
                className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 hover:border-emerald-500/40 hover:-translate-y-0.5 transition-all duration-300">
                <div className="h-11 w-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4">
                  <d.icon className="h-5 w-5 text-emerald-400" />
                </div>
                <h3 className="text-base font-bold tracking-tight">{d.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12"><Cta testid="value-cta" label="Build my free plan" /></div>
        </div>
      </section>

      {/* Why free / who it's for */}
      <section id="why" className="py-20 px-4 sm:px-8 scroll-mt-20">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/70 p-7 sm:p-8">
            <h3 className="font-display text-xl font-bold tracking-tight">Why we give this away free</h3>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              Simple: most owners have never seen their business laid out in numbers like this, and the plan alone is often worth thousands. We'd rather hand you something genuinely valuable, no strings attached, and let it speak for itself. If you ever want a hand putting it into action, you'll know where to find us — but the plan is yours to run with either way.
            </p>
            <div className="mt-5 space-y-2">
              {["No credit card, ever", "No obligation, no pushy call", "The plan is yours to keep"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-300">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> {t}
                </div>
              ))}
            </div>
          </div>
          <div className="relative overflow-hidden rounded-3xl border border-slate-800">
            <img src={DOJO_IMG} alt="Martial arts training" className="absolute inset-0 h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/40" />
            <div className="relative p-7 sm:p-8">
              <h3 className="font-display text-xl font-bold tracking-tight">Who it's for</h3>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">Owners of martial arts schools, gyms, and studios of any style who are great at teaching — but know their marketing and follow-up could be filling a lot more classes.</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {["Karate", "BJJ", "Taekwondo", "Kickboxing", "MMA", "Fitness & gyms"].map((t) => (
                  <span key={t} className="text-xs font-medium bg-slate-800/80 border border-slate-700 rounded-full px-3 py-1 text-slate-300">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 sm:px-8 scroll-mt-20">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight text-center mb-10">Questions, answered</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid="faq-accordion-item"
                className="border border-slate-800 rounded-2xl bg-slate-900/70 px-5 overflow-hidden">
                <AccordionTrigger className="text-left text-slate-100 font-semibold hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 relative overflow-hidden border-t border-emerald-500/20 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-64 w-[40rem] bg-emerald-500/15 blur-3xl rounded-full" aria-hidden />
        <div className="relative max-w-2xl mx-auto text-center">
          <MiraAvatar className="h-16 w-16 mx-auto mb-6" />
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            Stop guessing. See exactly what's holding your school back.
          </h2>
          <p className="mt-4 text-slate-300">It's free, it takes five minutes, and you'll walk away with a plan worth far more than that. Mira's ready when you are.</p>
          <div className="mt-8 flex justify-center">
            <Cta testid="final-cta-button" label="Get my free growth plan" />
          </div>
          <p className="mt-4 text-xs text-slate-500">No credit card · No sales call · Your plan is yours to keep</p>
        </div>
      </section>

      <footer className="py-8 px-4 border-t border-slate-800/80 text-center">
        <div className="flex justify-center mb-3"><Logo size="sm" /></div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} Mira · Powered by The Follow Up Ninja. Growth figures are illustrative examples, not guarantees.</p>
      </footer>
    </div>
  );
}

function Bubble({ who, children }) {
  const isMira = who === "mira";
  return (
    <div className={`flex items-end gap-2 ${isMira ? "justify-start" : "justify-end"}`}>
      {isMira && <MiraAvatar className="h-8 w-8 mb-1" />}
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${
        isMira ? "bg-slate-900 border border-slate-800 text-slate-200 rounded-bl-md" : "bg-emerald-500 text-slate-950 font-medium rounded-br-md"
      }`}>
        {children}
      </div>
    </div>
  );
}
